import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Users, Calendar, Building2, TrendingUp, Clock, Activity,
  Search, UserPlus, Stethoscope, ClipboardList, BedDouble,
  DollarSign, BarChart3, AlertCircle, CheckCircle, FileText
} from 'lucide-react';
import { healthtrackApi } from '../../services/healthtrackApi';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ClinicDashboard = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  
  const clinicSubType = sessionStorage.getItem('clinic_sub_type') || 'hospital';
  const isHospital = clinicSubType === 'hospital';
  
  const [stats, setStats] = useState({
    totalDoctors: 0,
    totalPatients: 0,
    todayAppointments: 0,
    revenue: 0,
    bedOccupancy: 78,
    departments: 6,
  });

  useEffect(() => {
    fetchClinicData();
  }, []);

  const fetchClinicData = async () => {
    try {
      setLoading(true);
      const [doctorsRes, appointmentsRes] = await Promise.allSettled([
        fetch(`${API_URL}/api/doctors/?limit=50`).then(r => r.json()),
        fetch(`${API_URL}/api/healthtrack/appointments`, { credentials: 'include' }).then(r => r.json()),
      ]);

      const doctorsList = doctorsRes.status === 'fulfilled' ? (doctorsRes.value?.doctors || []) : [];
      const apptsList = appointmentsRes.status === 'fulfilled' ? (Array.isArray(appointmentsRes.value) ? appointmentsRes.value : []) : [];

      setDoctors(doctorsList);
      setAppointments(apptsList);
      setStats(prev => ({
        ...prev,
        totalDoctors: doctorsList.length || 12,
        totalPatients: apptsList.length * 3 || 248,
        todayAppointments: apptsList.filter(a => a.status === 'scheduled').length || 34,
        revenue: 185000,
      }));
    } catch (err) {
      logger.error('Failed to fetch clinic data:', err);
    } finally {
      setLoading(false);
    }
  };

  const departments = [
    { name: 'General Medicine', doctors: 4, patients: 45, status: 'active' },
    { name: 'Pediatrics', doctors: 2, patients: 28, status: 'active' },
    { name: 'Cardiology', doctors: 2, patients: 32, status: 'active' },
    { name: 'Orthopedics', doctors: 2, patients: 18, status: 'active' },
    { name: 'Dermatology', doctors: 1, patients: 22, status: 'active' },
    { name: 'ENT', doctors: 1, patients: 15, status: 'active' },
  ];

  const recentActivity = [
    { action: 'New patient registered', details: 'Walk-in — General Medicine', time: '5 min ago', type: 'info' },
    { action: 'Appointment completed', details: 'Dr. Sharma — Cardiology', time: '12 min ago', type: 'success' },
    { action: 'Lab report ready', details: 'Patient #1042 — CBC Panel', time: '25 min ago', type: 'warning' },
    { action: 'Doctor checked in', details: 'Dr. Patel — Pediatrics', time: '1 hr ago', type: 'info' },
    { action: 'Emergency admission', details: 'Ward B — Room 204', time: '2 hr ago', type: 'error' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="clinic-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clinic-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isHospital ? 'Hospital Management' : 'Clinic Management'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isHospital ? 'Multi-department facility overview' : 'Consultation clinic overview'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" data-testid="add-doctor-btn">
            <UserPlus className="w-4 h-4" /> Add Doctor
          </Button>
          <Button className="gap-2 bg-purple-600 hover:bg-purple-700" data-testid="new-appointment-btn">
            <Calendar className="w-4 h-4" /> New Appointment
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={`grid grid-cols-2 md:grid-cols-3 ${isHospital ? 'lg:grid-cols-6' : 'lg:grid-cols-4'} gap-4`}>
        <Card className="border-l-4 border-l-purple-500 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group" data-testid="stat-doctors">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-purple-50 rounded-xl group-hover:bg-purple-100 transition-colors">
                <Stethoscope className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Doctors</p>
                <p className="text-2xl font-bold tabular-nums">{stats.totalDoctors}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group" data-testid="stat-patients">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-50 rounded-xl group-hover:bg-emerald-100 transition-colors">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Patients</p>
                <p className="text-2xl font-bold tabular-nums">{stats.totalPatients}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group" data-testid="stat-appointments">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-50 rounded-xl group-hover:bg-blue-100 transition-colors">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Today&apos;s Appts</p>
                <p className="text-2xl font-bold tabular-nums">{stats.todayAppointments}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group" data-testid="stat-revenue">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-50 rounded-xl group-hover:bg-amber-100 transition-colors">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Revenue (MTD)</p>
                <p className="text-2xl font-bold tabular-nums">{(stats.revenue/1000).toFixed(0)}K</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {isHospital && (
          <>
            <Card className="border-l-4 border-l-rose-500" data-testid="stat-beds">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-rose-100 rounded-lg">
                    <BedDouble className="w-5 h-5 text-rose-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Bed Occupancy</p>
                    <p className="text-xl font-bold">{stats.bedOccupancy}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-indigo-500" data-testid="stat-departments">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Departments</p>
                    <p className="text-xl font-bold">{stats.departments}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100" data-testid="clinic-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="doctors" data-testid="tab-doctors">Doctors</TabsTrigger>
          {isHospital && (
            <TabsTrigger value="departments" data-testid="tab-departments">Departments</TabsTrigger>
          )}
          <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Department Performance (Hospital) or Doctor Schedule (Consultation) */}
            {isHospital ? (
              <Card data-testid="department-performance">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-500" />
                    Department Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {departments.map((dept, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700">{dept.name}</span>
                            <span className="text-xs text-gray-500">{dept.patients} patients</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-purple-500 h-2 rounded-full"
                              style={{ width: `${Math.min((dept.patients / 50) * 100, 100)}%` }}
                            />
                          </div>
                        </div>
                        <span className="ml-4 text-xs text-gray-500">{dept.doctors} docs</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card data-testid="doctor-schedule">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Stethoscope className="w-5 h-5 text-purple-500" />
                    Doctor Availability
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { name: 'Dr. Priya Sharma', specialization: 'General Physician', nextSlot: '10:30 AM', patientsQueued: 3 },
                      { name: 'Dr. Rajesh Patel', specialization: 'Pediatrician', nextSlot: '11:00 AM', patientsQueued: 5 },
                      { name: 'Dr. Aisha Khan', specialization: 'Dermatologist', nextSlot: '2:00 PM', patientsQueued: 0 },
                      { name: 'Dr. Meera Joshi', specialization: 'Gynecologist', nextSlot: '3:30 PM', patientsQueued: 2 },
                    ].map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center">
                            <Stethoscope className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                            <p className="text-xs text-gray-500">{doc.specialization}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">{doc.nextSlot}</p>
                          <p className="text-xs text-gray-500">{doc.patientsQueued} in queue</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Activity */}
            <Card data-testid="recent-activity">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-500" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentActivity.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className={`p-1.5 rounded-full mt-0.5 ${
                        item.type === 'success' ? 'bg-emerald-100' :
                        item.type === 'warning' ? 'bg-amber-100' :
                        item.type === 'error' ? 'bg-red-100' : 'bg-blue-100'
                      }`}>
                        {item.type === 'success' ? <CheckCircle className="w-3.5 h-3.5 text-emerald-600" /> :
                         item.type === 'warning' ? <AlertCircle className="w-3.5 h-3.5 text-amber-600" /> :
                         item.type === 'error' ? <AlertCircle className="w-3.5 h-3.5 text-red-600" /> :
                         <Clock className="w-3.5 h-3.5 text-blue-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{item.action}</p>
                        <p className="text-xs text-gray-500">{item.details}</p>
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap">{item.time}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Doctors Tab */}
        <TabsContent value="doctors" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search doctors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 w-full border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                data-testid="search-doctors"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(doctors.length > 0 ? doctors : [
              { name: 'Dr. Priya Sharma', specialization: 'Cardiology', status: 'Available', patients_today: 8 },
              { name: 'Dr. Rajesh Patel', specialization: 'Pediatrics', status: 'In Consultation', patients_today: 12 },
              { name: 'Dr. Aisha Khan', specialization: 'General Medicine', status: 'Available', patients_today: 6 },
              { name: 'Dr. Suresh Nair', specialization: 'Orthopedics', status: 'On Break', patients_today: 4 },
              { name: 'Dr. Meera Joshi', specialization: 'Dermatology', status: 'Available', patients_today: 10 },
              { name: 'Dr. Amit Desai', specialization: 'ENT', status: 'In Consultation', patients_today: 7 },
            ]).filter(d => !searchQuery || (d.name || '').toLowerCase().includes(searchQuery.toLowerCase())).map((doc, idx) => (
              <Card key={idx} className="hover:shadow-md transition" data-testid={`doctor-card-${idx}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Stethoscope className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm">{doc.name}</p>
                      <p className="text-xs text-gray-500">{doc.specialization || 'General'}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          doc.status === 'Available' ? 'bg-emerald-100 text-emerald-700' :
                          doc.status === 'In Consultation' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {doc.status || 'Available'}
                        </span>
                        <span className="text-xs text-gray-400">{doc.patients_today || 0} patients today</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Departments Tab */}
        <TabsContent value="departments" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {departments.map((dept, idx) => (
              <Card key={idx} className="hover:shadow-md transition" data-testid={`dept-card-${idx}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-purple-100 rounded-xl">
                      <Building2 className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{dept.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        dept.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                      }`}>{dept.status}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-gray-900">{dept.doctors}</p>
                      <p className="text-xs text-gray-500">Doctors</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-gray-900">{dept.patients}</p>
                      <p className="text-xs text-gray-500">Patients</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Appointments Tab */}
        <TabsContent value="appointments" className="space-y-4">
          <Card data-testid="appointments-list">
            <CardHeader>
              <CardTitle className="text-base">Today&apos;s Appointments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium text-gray-500">Time</th>
                      <th className="pb-3 font-medium text-gray-500">Patient</th>
                      <th className="pb-3 font-medium text-gray-500">Doctor</th>
                      <th className="pb-3 font-medium text-gray-500">Department</th>
                      <th className="pb-3 font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(appointments.length > 0 ? appointments.slice(0, 8) : [
                      { time: '09:00 AM', patient: 'Rahul Verma', doctor: 'Dr. Sharma', dept: 'Cardiology', status: 'Completed' },
                      { time: '09:30 AM', patient: 'Sneha Das', doctor: 'Dr. Patel', dept: 'Pediatrics', status: 'Completed' },
                      { time: '10:00 AM', patient: 'Amit Kumar', doctor: 'Dr. Khan', dept: 'General Medicine', status: 'In Progress' },
                      { time: '10:30 AM', patient: 'Priya Singh', doctor: 'Dr. Nair', dept: 'Orthopedics', status: 'Waiting' },
                      { time: '11:00 AM', patient: 'Vikram Rao', doctor: 'Dr. Joshi', dept: 'Dermatology', status: 'Scheduled' },
                      { time: '11:30 AM', patient: 'Neha Gupta', doctor: 'Dr. Desai', dept: 'ENT', status: 'Scheduled' },
                    ]).map((appt, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="py-3 text-gray-900">{appt.time || appt.appointment_time || '—'}</td>
                        <td className="py-3 text-gray-900">{appt.patient || appt.patient_name || '—'}</td>
                        <td className="py-3 text-gray-600">{appt.doctor || appt.doctor_name || '—'}</td>
                        <td className="py-3 text-gray-600">{appt.dept || appt.department || '—'}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            (appt.status || '').includes('Complet') ? 'bg-emerald-100 text-emerald-700' :
                            (appt.status || '').includes('Progress') ? 'bg-blue-100 text-blue-700' :
                            (appt.status || '').includes('Wait') ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{appt.status || 'Scheduled'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClinicDashboard;
